/**
 * FRONT-2363: tell a finished RECONNECT apart from the connection that was
 * already there. The providers list only says a provider is connected, which
 * is just as true of the dead connection being replaced, so the connect flow
 * compares this fingerprint of the provider's connection row before and
 * after: a reconnect is recorded when the row's identity or last-update time
 * moves. Null when the provider has no row.
 */
export interface ConnectionRowLike {
  id?: string;
  nangoConnectionId?: string;
  provider?: string;
  providerUniqueKey?: string;
  updatedAt?: string | null;
}

export function connectionFingerprint(
  rows: ConnectionRowLike[],
  provider: { uniqueKey: string; provider: string },
): string | null {
  // By key first. The family fallback (rows written without a key) only
  // applies when exactly one row shares the family: Outlook and SharePoint
  // are both "microsoft", and binding to the sibling would judge the wrong
  // connection.
  const family = rows.filter((r) => r.provider === provider.provider);
  const row =
    rows.find((r) => r.providerUniqueKey === provider.uniqueKey) ?? (family.length === 1 ? family[0] : undefined);
  if (!row) return null;
  return JSON.stringify([row.id ?? null, row.nangoConnectionId ?? null, row.updatedAt ?? null]);
}

/**
 * Has the connection changed since `before`? An unknown starting point
 * (`undefined`: the first read failed) never counts as changed — waiting for
 * the user to close the tab is safer than declaring success on the old row.
 */
export function connectionChanged(before: string | null | undefined, after: string | null): boolean {
  if (before === undefined) return false;
  return after !== null && after !== before;
}

/**
 * The two checks the connect flow polls with. A first-time connect succeeds
 * the moment the provider is listed; a reconnect only when its row changed.
 */
export function connectChecks(opts: {
  reconnecting: boolean;
  before: string | null | undefined;
  readFingerprint: () => Promise<string | null | undefined>;
  isListed: () => Promise<boolean>;
}): { isConnected: () => Promise<boolean>; isConnectedAfterClose: () => Promise<boolean> } {
  if (!opts.reconnecting) return { isConnected: opts.isListed, isConnectedAfterClose: opts.isListed };
  // A reconnect is judged by the row changing both while the tab is open and
  // after it closes: "still listed" is exactly as true of the dead connection
  // the user gave up on. The integrations service stamps the row's
  // `updatedAt` on every creation/override webhook, so a finished reconnect
  // does change it.
  const changed = async () => {
    const after = await opts.readFingerprint();
    return after !== undefined && connectionChanged(opts.before, after);
  };
  return { isConnected: changed, isConnectedAfterClose: changed };
}
